import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  IAddSourceUrlsRequest,
  ICreateSourceRequest,
  IUpdateSourceRequest,
  PropertyItem,
  PropertyListResponse,
  SourceItem,
  UrlItem,
} from '../../../models';
import {
  SelectorPreviewRequest,
  SelectorPreviewResponse,
} from '../../../models/preview';
import { PropertyDetailResponse } from './propertyService';

// Create axios instance with base URL and credentials for cookies
const api: AxiosInstance = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': 'none', // Placeholder to satisfy dual identity source authorizer
  },
  withCredentials: true, // Important: enables cookies
});

// Add response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Don't redirect if we're already on the login page
      const isOnLoginPage = window.location.pathname === '/login';

      if (!isOnLoginPage) {
        // Redirect to login page
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Generic success message response
interface SuccessResponse {
  message: string;
}

// Sources API
export const sourcesApi = {
  getAll: (): Promise<AxiosResponse<SourceItem[]>> => api.get('/sources'),

  getById: (id: string): Promise<AxiosResponse<SourceItem>> =>
    api.get(`/sources/${id}`),

  create: (data: ICreateSourceRequest): Promise<AxiosResponse<SourceItem>> =>
    api.post('/sources', data),

  update: (
    id: string,
    data: IUpdateSourceRequest
  ): Promise<AxiosResponse<SourceItem>> => api.put(`/sources/${id}`, data),

  delete: (id: string): Promise<AxiosResponse<SuccessResponse>> =>
    api.delete(`/sources/${id}`),

  getUrls: (
    id: string,
    status?: string,
    active?: boolean
  ): Promise<AxiosResponse<UrlItem[]>> => {
    const params: Record<string, string> = {};
    if (status) {
      params.status = status;
    }
    if (active !== undefined) {
      params.active = String(active);
    }
    return api.get(`/sources/${id}/urls`, { params });
  },

  addUrls: (
    id: string,
    urls: string[]
  ): Promise<AxiosResponse<SuccessResponse>> =>
    api.post(`/sources/${id}/urls`, { urls } as IAddSourceUrlsRequest),

  process: (id: string): Promise<AxiosResponse<SuccessResponse>> =>
    api.post(`/sources/${id}/process`, {}),

  resetStatus: (id: string): Promise<AxiosResponse<SuccessResponse>> =>
    api.post(`/sources/${id}/reset-status`, {}),

  previewSelectors: (
    data: SelectorPreviewRequest
  ): Promise<AxiosResponse<SelectorPreviewResponse>> =>
    api.post('/selectors/preview', data),

};

// Properties API
export const propertiesApi = {
  getAll: (params?: {
    limit?: number;
    nextToken?: string;
  }): Promise<AxiosResponse<PropertyListResponse>> => {
    const queryParams = params
      ? `?${new URLSearchParams(params as any).toString()}`
      : '';
    return api.get(`/properties${queryParams}`);
  },

  getById: (
    id: string,
    sourceId: string
  ): Promise<AxiosResponse<PropertyItem>> =>
    api.get(`/sources/${sourceId}/properties/${id}`),

  getByIdAndSource: (
    id: string,
    sourceId: string
  ): Promise<AxiosResponse<PropertyDetailResponse>> =>
    api.get(`/sources/${sourceId}/properties/${id}`),

  getBySource: (
    sourceId: string,
    params?: {
      limit?: number;
      nextToken?: string;
    }
  ): Promise<AxiosResponse<PropertyListResponse>> => {
    const queryParams = params
      ? `?${new URLSearchParams(params as any).toString()}`
      : '';
    return api.get(`/sources/${sourceId}/properties${queryParams}`);
  },

  delete: (
    id: string,
    sourceId: string
  ): Promise<AxiosResponse<SuccessResponse>> =>
    api.delete(`/sources/${sourceId}/properties/${id}`),

  reject: (id: string): Promise<AxiosResponse<SuccessResponse>> =>
    api.post(`/properties/${id}/reject`, {}),

  getCount: (): Promise<
    AxiosResponse<{ active: number; inactive: number; total: number }>
  > => api.get('/properties/count'),

  recalculateCounts: (): Promise<
    AxiosResponse<{
      message: string;
      counts: { active: number; inactive: number; total: number };
    }>
  > => api.post('/properties/count/recalculate'),

  update: (
    id: string,
    sourceId: string,
    updates: Partial<PropertyItem>
  ): Promise<AxiosResponse<{ property: PropertyItem }>> =>
    api.put(`/sources/${sourceId}/properties/${id}`, updates),
};

// URLs API
export const urlsApi = {
  test: (sourceId: string, sourceUrl: string): Promise<AxiosResponse<any>> =>
    api.post(`/sources/${sourceId}/urls/test`, { sourceUrl }),

  parse: (sourceId: string, sourceUrl: string): Promise<AxiosResponse<any>> =>
    api.post(`/sources/${sourceId}/urls/parse`, { sourceUrl }),

  process: (
    sourceId: string,
    urlHash: string
  ): Promise<AxiosResponse<SuccessResponse>> =>
    api.post(`/sources/${sourceId}/urls/${urlHash}/process`, {}),

  delete: (
    sourceId: string,
    urlHash: string
  ): Promise<AxiosResponse<SuccessResponse>> =>
    api.delete(`/sources/${sourceId}/urls/${urlHash}`),

  updateUrl: (
    sourceId: string,
    urlHash: string,
    data: Record<string, any>
  ): Promise<AxiosResponse<SuccessResponse>> =>
    api.put(`/sources/${sourceId}/urls/${urlHash}`, data),
};

// Auth API
export const authApi = {
  login: (email: string, password: string): Promise<AxiosResponse<any>> =>
    api.post('/auth/login', { email, password }),

  logout: (): Promise<AxiosResponse<any>> => api.post('/auth/logout'),

  validate: (): Promise<AxiosResponse<any>> => api.get('/auth/validate'),

  createApiKey: (keyName: string, email: string): Promise<AxiosResponse<any>> =>
    api.post('/auth/keys', { keyName, email }),

  getApiKeysForUser: (email: string): Promise<AxiosResponse<any>> =>
    api.get(`/auth/user/${email}/keys`),

  deleteApiKey: (keyValue: string): Promise<AxiosResponse<any>> =>
    api.delete(`/auth/keys/${keyValue}`),
};

export default api;
